/**
 * Liner del short (snippets/liner-option.liquid)
 *
 * 1. "Sin liner / Con liner": con liner se muestran sus colores y su precio con descuento (data-discount) se suma al
 *    precio de arriba (con el precio sin descuento tachado) y al botón (data-atc-extra, como los complementos).
 * 2. La elección (con/sin y color) se recuerda al cambiar de color o talla del short: el tema vuelve a pintar la ficha.
 * 3. Al añadir el short al carrito (evento variant:add del formulario) se añade el liner en la misma cantidad, con la
 *    propiedad _pack_discount para que la función de descuento del carrito aplique el %.
 * 4. Si en "Opciones de compra" se elige un pack, la fila se oculta y no se añade (el pack ya lleva su liner).
 */
(() => {
  if (window.customElements.get("liner-option")) return;

  // Estado por formulario: se conserva cuando el tema vuelve a pintar la ficha
  const states = new Map();
  const boundForms = new Set();

  const moneyFormat = () =>
    (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || "{{amount}}";

  function formatMoney(cents) {
    const value = Math.round(Number(cents || 0));
    const withDelimiters = (decimals, thousands, decimal) => {
      const [whole, fraction] = (value / 100).toFixed(decimals).split(".");
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    const html = moneyFormat().replace(/\{\{\s*(\w+)\s*\}\}/, (_match, key) => {
      switch (key) {
        case "amount_no_decimals": return withDelimiters(0, ",", ".");
        case "amount_with_comma_separator": return withDelimiters(2, ".", ",");
        case "amount_no_decimals_with_comma_separator": return withDelimiters(0, ".", ",");
        case "amount_with_apostrophe_separator": return withDelimiters(2, "'", ".");
        case "amount_with_space_separator": return withDelimiters(2, " ", ",");
        default: return withDelimiters(2, ",", ".");
      }
    });
    const holder = document.createElement("div");
    holder.innerHTML = html;
    return holder.textContent;
  }

  function parseMoney(text) {
    const digits = String(text || "").replace(/[^\d]/g, "");
    if (!digits) return 0;
    return /no_decimals/.test(moneyFormat()) ? Number(digits) * 100 : Number(digits);
  }

  // Igual que en assets/pairs-addons.js (si ese bloque está en la página, ya la ha definido)
  if (!window.velitesUpdateAtcPrice) {
    window.velitesUpdateAtcPrice = (section) => {
      const extra = Array.from(section.querySelectorAll("[data-atc-extra]"))
        .reduce((sum, element) => sum + (Number(element.dataset.atcExtra) || 0), 0);
      section.querySelectorAll("[data-atc-price]").forEach((price) => {
        const shown = price.textContent;
        if (price.dataset.pairsLast !== shown) price.dataset.pairsBase = String(parseMoney(shown));
        const next = formatMoney(Number(price.dataset.pairsBase || 0) + extra);
        if (next !== shown) price.textContent = next;
        price.dataset.pairsLast = next;
      });
      return extra;
    };
  }

  class LinerOption extends HTMLElement {
    connectedCallback() {
      this.section = this.closest(".shopify-section") || document;
      this.toggles = Array.from(this.querySelectorAll('input[name^="liner-option-"]'));
      this.colors = Array.from(this.querySelectorAll('input[name^="liner-color-"]'));
      this.colorsBox = this.querySelector("[data-liner-colors]");

      // Recuperar la elección anterior (mismo color si existe en la nueva talla)
      const state = states.get(this.dataset.form) || {};
      const on = this.toggles.find((input) => input.value === "on");
      if (state.lined && on && !on.disabled) on.checked = true;
      const remembered = this.colors.find((input) => input.dataset.color === state.color && !input.disabled);
      const firstAvailable = this.colors.find((input) => !input.disabled);
      const initialColor = remembered || firstAvailable;
      if (initialColor) initialColor.checked = true;

      this.toggles.forEach((input) => input.addEventListener("change", () => this.update()));
      this.colors.forEach((input) => input.addEventListener("change", () => this.update()));

      this.watchPackMode();
      this.bindForm();
      this.update();
    }

    disconnectedCallback() {
      this._packObserver?.disconnect();
    }

    get lined() {
      const checked = this.toggles.find((input) => input.checked);
      return Boolean(checked && checked.value === "on") && !this.packMode;
    }

    get selectedColor() {
      return this.colors.find((input) => input.checked && !input.disabled) || null;
    }

    // "Opciones de compra" con un pack elegido (la primera tarjeta es el artículo suelto)
    get packMode() {
      const options = Array.from(this.section.querySelectorAll(".promo-pack-list__option"));
      return options.length > 1 && !options[0].classList.contains("is-selected");
    }

    watchPackMode() {
      const list = this.section.querySelector(".promo-pack-list");
      if (!list) return;
      this._packObserver = new MutationObserver(() => this.update());
      this._packObserver.observe(list, { subtree: true, attributes: true, attributeFilter: ["class"] });
    }

    update() {
      const packMode = this.packMode;
      this.hidden = packMode;

      const lined = this.lined;
      const color = this.selectedColor;
      if (this.colorsBox) this.colorsBox.hidden = !lined;

      const selectedLabel = this.querySelector("[data-liner-selected]");
      if (selectedLabel) selectedLabel.textContent = lined ? this.dataset.onLabel : this.dataset.offLabel;
      const colorName = this.querySelector("[data-liner-color-name]");
      if (colorName && color) colorName.textContent = color.dataset.color;

      // Si en esta talla no hay liner (p. ej. 2XL), se recuerda la elección anterior para la siguiente talla
      const previous = states.get(this.dataset.form) || {};
      const on = this.toggles.find((input) => input.value === "on");
      const available = Boolean(on && !on.disabled);
      states.set(this.dataset.form, {
        lined: available ? Boolean(on.checked) : Boolean(previous.lined),
        color: color ? color.dataset.color : previous.color || null,
      });

      // Precio del liner (con descuento) en el botón y en el precio de arriba
      const linerFull = lined && color ? Number(color.dataset.price || 0) : 0;
      const linerPrice = this.discounted(linerFull);
      this.dataset.atcExtra = String(linerPrice);
      window.velitesUpdateAtcPrice(this.section);
      this.updateSummaryPrice(linerFull, linerPrice);
    }

    get discount() {
      return Math.min(90, Math.max(0, parseInt(this.dataset.discount, 10) || 0));
    }

    // Igual que la función de descuento del carrito: el descuento se redondea hacia abajo
    discounted(cents) {
      return cents - Math.floor((cents * this.discount) / 100);
    }

    // Precio junto a "Color:" (móvil): mismo cálculo, con el precio sin descuento tachado delante
    updatePickerPrice(linerFull, linerPrice) {
      this.section.querySelectorAll(".variant-picker__option-price").forEach((box) => {
        if (box.dataset.linerOriginal === undefined) box.dataset.linerOriginal = box.innerHTML;
        if (!linerFull) {
          if (box.dataset.linerApplied) {
            box.innerHTML = box.dataset.linerOriginal;
            delete box.dataset.linerApplied;
          }
          return;
        }
        const base = Number(this.dataset.basePrice || 0);
        const baseCompare = Math.max(base, Number(this.dataset.baseCompare || 0));
        const total = base + linerPrice;
        const compare = baseCompare + linerFull;
        box.innerHTML = total < compare
          ? `<s class="variant-picker__option-price-compare">${formatMoney(compare)}</s> <span class="variant-picker__option-price-current is-sale">${formatMoney(total)}</span>`
          : `<span class="variant-picker__option-price-current">${formatMoney(total)}</span>`;
        box.dataset.linerApplied = "1";
      });
    }

    // Precio de arriba: short + liner con descuento y, tachado, short + liner sin descuento
    updateSummaryPrice(linerFull, linerPrice) {
      this.updatePickerPrice(linerFull, linerPrice);
      const box = this.section.querySelector(".product-summary__price");
      if (!box) return;
      if (box.dataset.linerOriginal === undefined) box.dataset.linerOriginal = box.innerHTML;
      if (!linerFull) {
        if (box.dataset.linerApplied) {
          box.innerHTML = box.dataset.linerOriginal;
          delete box.dataset.linerApplied;
        }
        return;
      }
      const base = Number(this.dataset.basePrice || 0);
      const baseCompare = Math.max(base, Number(this.dataset.baseCompare || 0));
      const total = base + linerPrice;
      const compare = baseCompare + linerFull;
      const priceList = document.createElement("div");
      priceList.innerHTML = box.dataset.linerOriginal;
      const sale = priceList.querySelector("sale-price");
      if (sale) {
        sale.className = sale.className.replace(/\btext-subdued\b/, "").trim();
        if (total < compare && !/text-on-sale/.test(sale.className)) sale.classList.add("text-on-sale");
        // Se deja solo el texto para lectores de pantalla (.sr-only) y se pone el importe nuevo
        Array.from(sale.childNodes).forEach((node) => { if (!(node.classList && node.classList.contains("sr-only"))) node.remove(); });
        sale.append(formatMoney(total));
      }
      let compareEl = priceList.querySelector("compare-at-price");
      if (total < compare) {
        if (!compareEl && sale) {
          compareEl = document.createElement("compare-at-price");
          compareEl.className = "text-subdued line-through";
          sale.after(compareEl);
        }
        if (compareEl) {
          Array.from(compareEl.childNodes).forEach((node) => { if (!(node.classList && node.classList.contains("sr-only"))) node.remove(); });
          compareEl.append(formatMoney(compare));
        }
      }
      box.innerHTML = priceList.innerHTML;
      box.dataset.linerApplied = "1";
    }

    bindForm() {
      if (boundForms.has(this.dataset.form)) return;
      boundForms.add(this.dataset.form);

      const formId = this.dataset.form;
      // Al volver a pintar la ficha (otra variante) se vuelve a sumar el liner al botón
      document.addEventListener("product:rerender", (event) => {
        if (event.target && event.target.getAttribute && event.target.getAttribute("id") === formId) {
          setTimeout(() => document.querySelector(`liner-option[data-form="${CSS.escape(formId)}"]`)?.update(), 60);
        }
      }, true);

      document.addEventListener("variant:add", (event) => {
        const form = event.target && event.target.closest ? event.target.closest("form") : null;
        if (!form || form.getAttribute("id") !== formId) return;
        const current = document.querySelector(`liner-option[data-form="${CSS.escape(formId)}"]`);
        if (current) current.onVariantAdd(form);
      });
    }

    async onVariantAdd(form) {
      if (!this.lined) return;
      const color = this.selectedColor;
      if (!color) return;

      const quantityInput = form.querySelector('[name="quantity"]') || document.querySelector(`[name="quantity"][form="${CSS.escape(form.getAttribute("id"))}"]`);
      const quantity = Math.max(1, parseInt(quantityInput && quantityInput.value, 10) || 1);

      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        await fetch(`${root}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            items: [{
              id: Number(color.value),
              quantity,
              properties: this.discount > 0 ? { _pack_discount: String(this.discount) } : {},
            }],
          }),
        });
      } finally {
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      }
    }
  }

  window.customElements.define("liner-option", LinerOption);
})();
